// Libraries
import { Stack } from "@mui/material";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { IPauseReason, IUserPause, PauseActionType} from "@smartrr/shared/entities/CancellationRelationship";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { SubscriptionContractSubscriptionStatus } from "@smartrr/shared/shopifyGraphQL/api";
import { captureException } from "@smartrr/shared/utils/captureException";
import { adjustToStoreBillingTime, resetTimeOnDate } from "@smartrr/shared/utils/dateUtils";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import moment from "moment";
import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { pauseSubscription } from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { saveUserPauseReasonAPI } from "@customer-app/state/actionCreators/retention";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";

import { AquariusDatePicker } from "./AquariusDatePicker";
import { CenterText } from "./CenterText";
import { ValidViews } from "../OrderOptionsModalInner";
import PauseReasonPicker from "./PauseReasonPicker";
// Helpers & Utils

interface IPauseViewProps {
  purchaseState: IPurchaseState;
  initialView: boolean;

  setIconButtonInactive: () => void;
  returnToModal: (initialView?: ValidViews) => void;
}

export const PauseView = ({
  purchaseState,
  setIconButtonInactive,
  returnToModal,
  initialView,
  ...props
}: IPauseViewProps): JSX.Element => {
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const { organization, isLoading: isOrgLoading } = useSmartrrCustomerSelector(state => state.auth);
  const isPauseLoading = useSmartrrCustomerSelector(state => state.purchaseStates.isPauseLoading);
  const { isEnabled: hasRetention, pauseReasons } = useSmartrrCustomerSelector(state => state.retention);
  const localizeShorthand = "subscriptions.yourSubscriptions.manageSubscriptionModal";
  const initialPauseDate = useMemo(() => {
    if (purchaseState.unpauseDate) {
      return new Date(purchaseState.unpauseDate);
    }
    return moment().add(1, "M").toDate();
  }, []);

  const isRetentionAvailable =
    hasRetention && pauseReasons?.length && organization?.hasRetentionPauseReasonEnabled;

  const isPauseDateScreenInitial = useMemo(() => {
    return (purchaseState.purchaseStateStatus === "PAUSED" && purchaseState.unpauseDate) || initialView;
  }, [purchaseState.purchaseStateStatus, initialView]);

  const [selectedPauseReason, setSelectedPauseReason] = useState<IPauseReason>();
  const [userPauseReasonText, setUserPauseReasonText] = useState<string>();

  //if retention is disabled or retention step is complete -> true
  const [isPauseDateScreen, setPauseDateScreen] = useState(!isRetentionAvailable || isPauseDateScreenInitial);
  const [selectedTime, setSelectedTime] = useState<Date | null>(initialPauseDate);

  const stackRef = useRef<HTMLDivElement>(null);

  const tomorrow = useMemo(() => {
    const date = new Date();
    return new Date(date.setDate(date.getDate() + 1));
  }, []);
  const disabledDate = useMemo(() => resetTimeOnDate(tomorrow), []);

  const disabledDaysAfter = useMemo(() => {
    return moment().add(6, "M").toDate();
  }, [initialPauseDate]);

  const onPause = async (unpauseDate: Date) => {
    const adjustedUnpauseDate = adjustToStoreBillingTime(
      unpauseDate,
      organization?.billingTime,
      organization?.billingTimezone
    );
    await dispatch(pauseSubscription(purchaseState.id, adjustedUnpauseDate))
      .then(res => {
        if (res.type === "PURCHASE_STATE_PAUSE_ERROR") {
          toast.error(localize(`${localizeShorthand}.pauseView.pauseErrorToast`));
        } else if (res.type === "PURCHASE_STATE_PAUSE_SUCCESS") {
          if (purchaseState.unpauseDate) {
            toast(localize(`${localizeShorthand}.pauseView.pauseResumeDateUpdatedToast`));
          } else {
            toast(localize(`${localizeShorthand}.pauseView.pauseToast`));
          }
        }
      })
      .finally(() => closeModal());
  };

  const onTogglePause = () => {
    if (!selectedTime) {
      toast.error(
        typedLocalize("subscriptions.yourSubscriptions.manageSubscriptionModal.pauseView.pauseDateErrorToast")
      );
      return;
    }
    onPause(selectedTime);
    saveUserPauseData(userPauseReasonText || "", selectedPauseReason?.id);
  };

  const handleReasonTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setUserPauseReasonText(e.target.value);
    },
    [userPauseReasonText, setUserPauseReasonText]
  );

  const saveUserPauseData = useCallback(
    async (userPauseReasonText: string, id?: string) => {
      const userPause: IUserPause = {
        reasonText: userPauseReasonText || "",
        stId: purchaseState.id,
        pauseReasonId: id || "",
        actionType: PauseActionType.PAUSE,
      };

      await saveUserPauseReasonAPI(userPause)
        .then(res => {
          if (res.type === "error") {
            captureException("Error saving user's pause reason", res.message);
          }
        })
        .catch(error => {
          captureException("Error saving user's pause reason", error);
        });
    },
    [userPauseReasonText, selectedPauseReason, handleReasonTextChange]
  );

  const onCancel = useCallback(() => {
    if (!isRetentionAvailable || isPauseDateScreenInitial) {
      returnToModal("Default");
      //avoids errors when buttons are hidden
      if (purchaseState.purchaseStateStatus !== SubscriptionContractSubscriptionStatus.Paused) {
        setIconButtonInactive();
      }
      return;
    }
    setPauseDateScreen(false);
  }, [isPauseDateScreenInitial, isRetentionAvailable]);

  if (isPauseLoading || isOrgLoading) {
    return (
      <Stack justifyContent="center" alignItems="center" height={stackRef.current?.clientHeight + "px"}>
        <DotSpinner />
      </Stack>
    );
  }

  if (isPauseDateScreen) {
    return (
      <Stack ref={stackRef} spacing="15px" justifyContent="center" alignItems="center" sx={{ marginTop: "15px" }}>
        <CenterText
          title={localize(
            `${localizeShorthand}.pauseView.${
              purchaseState.purchaseStateStatus === "PAUSED" ? "changePauseDateTitle" : "selectPauseDateTitle"
            }`
          )}
        >
          {localize(`${localizeShorthand}.pauseView.pauseWithDateBody`)}
        </CenterText>

        <AquariusDatePicker
          initialMonth={initialPauseDate}
          disabledDaysBefore={disabledDate}
          //disable dates 6 month from today
          disabledDaysAfter={disabledDaysAfter}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          selectDate={setSelectedTime}
        />
        <ConfirmCancelButtons
          disabled={!selectedTime}
          cancelTextOverride={isRetentionAvailable && !isPauseDateScreenInitial ? "general.backBtn" : undefined}
          isLoading={isPauseLoading}
          onCancel={onCancel}
          onConfirm={onTogglePause}
        />
      </Stack>
    );
  }

  return (
    <Stack ref={stackRef} spacing="15px" justifyContent="center" alignItems="center" sx={{ marginTop: "15px" }}>
      <CenterText title={localize(`${localizeShorthand}.pauseView.pauseReasonTitle`)}>
        {localize(`${localizeShorthand}.pauseView.pauseBody`)}
      </CenterText>
      <PauseReasonPicker
        selectedPauseReason={selectedPauseReason}
        setSelectedPauseReason={setSelectedPauseReason}
        userPauseReasonText={userPauseReasonText}
        setUserPauseReasonText={setUserPauseReasonText}
        placeholderText={localize(`${localizeShorthand}.cancelView.textAreaPlaceholder`)}
      />
      <ConfirmCancelButtons
        confirmTextOverride={"general.nextBtn"}
        onCancel={setIconButtonInactive}
        onConfirm={() => setPauseDateScreen(true)}
      />
    </Stack>
  );
};
